import {
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { HttpHandler } from 'shared/httpHandler';

const CarMaintenanceContext = createContext();

const CarMaintenanceProvider = ({ children }) => {
  const [carTypes, setCarTypes] = useState([]);
  const [carCatalogs, setCarCatalogs] = useState([]);
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const httpHandler = new HttpHandler();

  useEffect(() => {
    getCarCatalogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const getCarTypes = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await httpHandler.get(
        '/toyota/show_room_maintenance/car_types'
      );

      if (response.success) {
        setCarTypes(response.data);
      } else {
        console.error('取得 Toyota 車型資料失敗:', response);
        setError('取得 Toyota 車型資料失敗');
      }
    } catch (error) {
      setError('取得 Toyota 車型資料時發生錯誤');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCarCatalogs = useCallback(
    async () => {
      console.log('getCarCatalogs searchParams:', searchParams);
      // handle is_cancel = 2
      if (searchParams.get('is_cancel') === '2') {
        searchParams.delete('is_cancel');
      }

      try {
        setIsLoading(true);
        const response = await httpHandler.get(
          '/toyota/show_room_maintenance/car_catalogs?' +
            searchParams.toString()
        );

        if (response.success) {
          setCarCatalogs(response.data);
        } else {
          console.error('取得 Toyota 車款資料失敗:', response);
          setError('取得 Toyota 車款資料失敗');
        }
      } catch (error) {
        setError('取得 Toyota 車款資料時發生錯誤');
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams]
  );

  // carType actions
  const createCarType = useCallback(
    async (carTypeData, carTypeId = null) => {
      try {
        setIsLoading(true);
        let response;
        if (carTypeId) {
          response = await httpHandler.put(
            `/toyota/show_room_maintenance/car_type/${carTypeId}`,
            carTypeData
          );
        } else {
          response = await httpHandler.post(
            '/toyota/show_room_maintenance/car_type',
            carTypeData
          );
        }

        if (response.success) {
          if (window.antdNotify) {
            window.antdNotify(
              'Toyota 車型',
              `${carTypeId ? '編輯' : '新增'} Toyota 車型資料成功`,
              'success'
            );
          } else {
            console.log('response:', response);
          }
        } else {
          setError(response.message || '新增 Toyota 車型資料失敗');
        }
      } catch (error) {
        setError('新增 Toyota 車型資料時發生錯誤');
      } finally {
        setIsLoading(false);
        await getCarTypes();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCarCatalogs]
  );

  const deleteCarType = useCallback(
    async (carTypeId) => {
      try {
        setIsLoading(true);
        const response = await httpHandler.delete(
          `/toyota/show_room_maintenance/car_type/${carTypeId}`
        );

        if (response.success) {
          if (window.antdNotify) {
            window.antdNotify(
              'Toyota 車型',
              '刪除 Toyota 車型資料成功',
              'success'
            );
          } else {
            console.log('response:', response);
          }
        } else {
          setError(response.message || '刪除 Toyota 車款資料失敗');
        }
      } catch (error) {
        setError('刪除 Toyota 車款資料時發生錯誤');
      } finally {
        setIsLoading(false);
        await getCarTypes();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCarCatalogs]
  );

  // carCatalog actions
  const createCarCatalog = useCallback(
    async (carCatalogData, carCatalogId = null) => {
      try {
        setIsLoading(true);
        let response;
        if (carCatalogId) {
          response = await httpHandler.put(
            `/toyota/show_room_maintenance/car_catalog/${carCatalogId}`,
            carCatalogData
          );
        } else {
          response = await httpHandler.post(
            '/toyota/show_room_maintenance/car_catalog',
            carCatalogData
          );
        }

        if (response.success) {
          if (window.antdNotify) {
            window.antdNotify(
              'Toyota 車款',
              `${carCatalogId ? '編輯' : '新增'} Toyota 車款資料成功`,
              'success'
            );
          } else {
            console.log('response:', response);
          }
          return response;
        } else {
          setError(response.message || '新增 Toyota 車款資料失敗');
        }
      } catch (error) {
        setError('新增 Toyota 車款資料時發生錯誤');
      } finally {
        setIsLoading(false);
        await getCarCatalogs();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCarCatalogs]
  );

  const deleteCarCatalog = useCallback(
    async (carCatalogId) => {
      try {
        setIsLoading(true);
        const response = await httpHandler.delete(
          `/toyota/show_room_maintenance/car_catalog/${carCatalogId}`
        );

        if (response.success) {
          if (window.antdNotify) {
            window.antdNotify(
              'Toyota 車款',
              '刪除 Toyota 車款資料成功',
              'success'
            );
          } else {
            console.log('response:', response);
          }
        } else {
          setError(response.message || '刪除 Toyota 車款資料失敗');
        }
      } catch (error) {
        setError('刪除 Toyota 車款資料時發生錯誤');
      } finally {
        setIsLoading(false);
        await getCarCatalogs();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCarCatalogs]
  );

  useEffect(() => {
    getCarTypes();
    getCarCatalogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CarMaintenanceContext.Provider
      value={{
        carTypes,
        carCatalogs,
        error,
        isLoading,
        getCarTypes,
        getCarCatalogs,
        createCarType,
        deleteCarType,
        createCarCatalog,
        deleteCarCatalog,
      }}>
      {children}
    </CarMaintenanceContext.Provider>
  );
};

export default CarMaintenanceProvider;

export const useCarMentenanceContext = () => {
  const context = useContext(CarMaintenanceContext);
  if (!context) {
    throw new Error(
      'useCarMentenanceContext must be used within a CarMaintenanceProvider'
    );
  }
  return context;
};
